/**
 * The qualifying questions behind every webinar registration.
 *
 * The order and the wording are fixed by the programme brief. Each option value
 * is the string written to HubSpot, so the scoring rules and the CRM properties
 * read the same vocabulary.
 */

export const INSTITUTION_TYPES = [
  'Ministry (national)',
  'Government agency, authority or commission',
  'Subnational or local government',
  'Central bank or revenue authority',
  'Development partner, DFI or donor',
  'Multilateral or UN body',
  'State owned enterprise',
  'Nonprofit working with government',
  'Other',
] as const

export const SENIORITY = [
  'Minister, deputy minister or permanent secretary',
  'Director general or agency head',
  'Director or head of unit',
  'Manager or coordinator',
  'Officer or specialist',
  'Adviser or consultant',
] as const

export const FUNCTIONS = [
  'Executive or cabinet',
  'Policy or planning',
  'Monitoring, evaluation or statistics',
  'Finance, budget or revenue',
  'Digital, ICT or data',
  'Communications or investment promotion',
  'Programme or service delivery',
  'Procurement or oversight',
] as const

export const CHALLENGES = [
  'Seeing what our plan is delivering',
  'Reporting to Cabinet or partners',
  'Turning our data into decisions',
  'Putting AI to work safely',
  'Raising or tracking revenue',
  'Not sure yet',
] as const

export const TIMELINES = ['Yes, actively', 'Yes, exploring', 'Just learning for now'] as const

export const DECISION_ROLES = [
  'I decide or approve',
  'I advise or recommend',
  'I am researching for others',
] as const

export const REFERRAL_SOURCES = [
  'A colleague',
  'LinkedIn',
  'Search',
  'An email from Cavalla',
  'A development partner',
  'An event or conference',
  'Other',
] as const

/** Countries grouped by region, so a long list stays navigable on a phone. */
export const COUNTRY_GROUPS: { region: string; countries: string[] }[] = [
  {
    region: 'West Africa',
    countries: [
      'Benin',
      'Burkina Faso',
      'Cabo Verde',
      'Côte d’Ivoire',
      'The Gambia',
      'Ghana',
      'Guinea',
      'Guinea-Bissau',
      'Liberia',
      'Mali',
      'Mauritania',
      'Niger',
      'Nigeria',
      'Senegal',
      'Sierra Leone',
      'Togo',
    ],
  },
  {
    region: 'East Africa',
    countries: [
      'Burundi',
      'Djibouti',
      'Eritrea',
      'Ethiopia',
      'Kenya',
      'Rwanda',
      'Somalia',
      'South Sudan',
      'Sudan',
      'Tanzania',
      'Uganda',
    ],
  },
  {
    region: 'Southern Africa',
    countries: [
      'Angola',
      'Botswana',
      'Eswatini',
      'Lesotho',
      'Madagascar',
      'Malawi',
      'Mauritius',
      'Mozambique',
      'Namibia',
      'South Africa',
      'Zambia',
      'Zimbabwe',
    ],
  },
  {
    region: 'Central Africa',
    countries: [
      'Cameroon',
      'Central African Republic',
      'Chad',
      'Republic of the Congo',
      'Democratic Republic of the Congo',
      'Equatorial Guinea',
      'Gabon',
      'São Tomé and Príncipe',
    ],
  },
  {
    region: 'North Africa',
    countries: ['Algeria', 'Egypt', 'Libya', 'Morocco', 'Tunisia'],
  },
  {
    region: 'Europe',
    countries: [
      'Belgium',
      'Denmark',
      'France',
      'Germany',
      'Ireland',
      'Italy',
      'Netherlands',
      'Norway',
      'Portugal',
      'Spain',
      'Sweden',
      'Switzerland',
      'United Kingdom',
    ],
  },
  {
    region: 'Rest of the world',
    countries: ['Australia', 'Brazil', 'Canada', 'China', 'India', 'Japan', 'United States', 'Other'],
  },
]

/**
 * Personal email domains. A personal address is flagged for review rather than
 * blocked, because an official on a personal device is still a real lead.
 */
const PERSONAL_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'icloud.com',
  'aol.com',
  'protonmail.com',
  'proton.me',
  'zoho.com',
  'gmx.com',
  'mail.com',
  'yandex.com',
]

export function isPersonalEmail(email: string): boolean {
  const domain = email.trim().toLowerCase().split('@')[1] ?? ''
  return PERSONAL_DOMAINS.includes(domain)
}

/** An institutional address, which is what routes a contact to the public sector pipeline. */
export function isInstitutionalEmail(email: string): boolean {
  const domain = email.trim().toLowerCase().split('@')[1] ?? ''
  if (!domain || isPersonalEmail(domain ? `x@${domain}` : '')) return false
  return /\.(gov|gov\.[a-z]{2}|go\.[a-z]{2}|gouv\.[a-z]{2}|govt\.[a-z]{2}|int|org|edu|ac\.[a-z]{2})$/.test(
    domain,
  )
}
